import React, { createContext, useContext, useState, useEffect } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  toggleTheme: () => {},
});

const THEME_STORAGE_KEY = 'delegate.theme';

function readStoredTheme(): Theme | null {
  try {
    const value = sessionStorage.getItem(THEME_STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

/**
 * Stamp the theme onto <html> SYNCHRONOUSLY, before React re-renders children.
 *
 * This used to live only in a useEffect, which React flushes *after* children
 * render. Anything that reads a theme token during render — chart-theme.ts and
 * KnowledgeGraphViewer both call getComputedStyle(document.documentElement) —
 * therefore saw the OUTGOING theme's values and painted one theme behind: on
 * the way back to light, charts drew grid lines and mark outlines in the dark
 * palette's near-black, which is what made them look thick and black until an
 * unrelated re-render corrected them.
 *
 * Setting the attribute at the same moment we change the state keeps the DOM
 * and React in step, so a read during render is always the incoming theme.
 */
function applyTheme(theme: Theme): Theme {
  document.documentElement.setAttribute('data-theme', theme);
  return theme;
}

/** Length of the palette crossfade. Mirrored in theme.css and in the
 *  marketing toggle (marketing/src/scripts/theme-toggle.ts). */
const THEME_FADE_MS = 800;
let fadeTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Arm the crossfade, then let the caller change the theme.
 *
 * Only ever called from toggleTheme. Startup and OS-preference changes stamp
 * the theme without it on purpose: fading in on first paint looks like a bug,
 * and a system-driven switch is not something the user asked to watch.
 *
 * The forced reflow is required, not defensive. Adding the class and flipping
 * data-theme in the same tick lets the browser collapse both into one style
 * pass, and a property that had no transition before the change may snap
 * instead of easing. Reading offsetHeight commits the transition rules first.
 */
function beginThemeFade(): void {
  const root = document.documentElement;
  root.classList.add('theme-fading');
  void root.offsetHeight;
  if (fadeTimer) clearTimeout(fadeTimer);
  fadeTimer = setTimeout(() => {
    root.classList.remove('theme-fading');
    fadeTimer = undefined;
  }, THEME_FADE_MS);
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Initialize from this tab's preference, then fall back to the OS.
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = readStoredTheme();
    if (savedTheme) {
      return applyTheme(savedTheme);
    }

    // Fall back to system preference
    return applyTheme(
      window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light',
    );
  });

  // Toggle theme function
  const toggleTheme = () => {
    setTheme((prevTheme) => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light';
      try {
        sessionStorage.setItem(THEME_STORAGE_KEY, newTheme);
      } catch {
        // A blocked storage area must not prevent the visible theme change.
      }
      beginThemeFade();
      return applyTheme(newTheme);
    });
  };

  // Safety net: keeps <html> correct if the state is ever changed by a path
  // that did not go through applyTheme. Normally a no-op.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't manually set a preference
      if (!readStoredTheme()) {
        setTheme(applyTheme(e.matches ? 'dark' : 'light'));
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
